import cn from 'classnames';

import { beGetSalonTaxesAndFees } from 'actions/salon/beGetSalonTaxesAndFees.action';
import Icon from 'components/generic/Icon.component';
import InlineSpinner from 'components/generic/InlineSpinner.component';
import { usePrepareReadOnly } from 'hooks/usePrepare.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';

import { AddRuleButton } from './AddRuleButton';
import { Card, CardBody, CardHeader } from './Card';
import { RuleCard } from './RuleCard';

import CN from './TaxesAndFeesLayout.module.scss';

const MAX_TAXES = 10;

export const TaxesAndFeesLayout = () => {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();

  const { qj, isLoading } = usePrepareReadOnly(beGetSalonTaxesAndFees);

  const taxes = qj?.taxes ?? [];
  const fees = qj?.fees ?? [];

  const numberOfTaxes = taxes.length;
  const numberOfFees = fees.length;

  const numberOfRules = numberOfTaxes + numberOfFees;

  const hasMaxRules = numberOfRules === MAX_TAXES;
  const hasNoRules = numberOfRules === 0;

  const sortedTaxes = taxes.sort((a, b) => a.name.localeCompare(b.name));
  const sortedFees = fees.sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div data-bem="TaxesAndFeesLayout" className={cn(CN.component, isDarkMode && CN.dark)}>
      <div className={CN.section}>
        <div className={CN.sectionHeader}>
          <div className={CN.sectionRow}>
            <div className={CN.sectionRowTitle}>
              <Icon variant="taxesAndFees" variantInvert width="l1" />
              <div className={CN.cardTitle}>{t('staffAndBusiness.taxesAndFees.title')}</div>
            </div>
            {!hasNoRules && <AddRuleButton isDisabled={hasMaxRules || isLoading} />}
          </div>
          {!hasNoRules && (
            <div className={CN.sectionRow}>
              <div className={CN.cardDescription}>{t('staffAndBusiness.taxesAndFees.maxTaxes', { maxTaxes: MAX_TAXES })}</div>
              <div className={CN.cardDescription}>{t('staffAndBusiness.taxesAndFees.currentTaxes', { currentTaxes: numberOfRules, maxTaxes: MAX_TAXES })}</div>
            </div>
          )}
        </div>
        {isLoading && <InlineSpinner />}
        {!isLoading && hasNoRules && (
          <Card data-bem="NoTaxesAndFeesCard" className={CN.noTaxesAndFeesCard}>
            <CardHeader>
              <Icon variant="alert" variantInvert width="l1" />
            </CardHeader>
            <CardBody>
              <div className={CN.noTaxesAndFeesBody}>
                <div className={CN.noTaxesAndFeesTitle}>{t('staffAndBusiness.taxesAndFees.noTaxesAndFeesTitle')}</div>
                <div className={CN.noTaxesAndFeesDescription}>{t('staffAndBusiness.taxesAndFees.noTaxesAndFeesDescription', { maxTaxes: MAX_TAXES })}</div>
                <AddRuleButton isDisabled={hasMaxRules || isLoading} />
              </div>
            </CardBody>
          </Card>
        )}
        {!isLoading && !hasNoRules && (
          <div className={CN.sectionBody}>
            {sortedTaxes.map((tax) => (
              <RuleCard key={tax.id} rule={tax} variant="tax" />
            ))}
            {sortedFees.map((fee) => (
              <RuleCard key={fee.id} rule={fee} variant="fee" />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
